import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/context/AuthContext';

export default function PayNow() {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState('manual');
  const [screenshot, setScreenshot] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState({
    manual_mobile_money_number: '',
    hubtel_enabled: false,
    tier_discount_percent: 0,
    loyalty_tier: 'bronze',
  });
  const [loading, setLoading] = useState(false);

  const loadItems = () => axios.get('/api/customer/items', { params: { payment_status: 'unpaid' } })
    .then((r) => setItems(r.data))
    .catch(() => {});

  useEffect(() => {
    // Refresh user so tier discount reflects current tier
    refreshUser();
    loadItems();
    axios.get('/api/customer/payments/settings')
      .then((r) => setPaymentSettings({
        manual_mobile_money_number: r.data.manual_mobile_money_number || '',
        hubtel_enabled: Boolean(r.data.hubtel_enabled),
        tier_discount_percent: Number(r.data.tier_discount_percent || 0),
        loyalty_tier: r.data.loyalty_tier || 'bronze',
      }))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!paymentSettings.hubtel_enabled && method === 'hubtel') {
      setMethod('manual');
    }
  }, [paymentSettings.hubtel_enabled, method]);

  const toggle = (id) => setSelected((current) => (
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
  ));

  const tier = paymentSettings.loyalty_tier || user?.loyalty_tier || 'bronze';
  const tierDiscountPct = Number(paymentSettings.tier_discount_percent || 0);

  const subtotal = items
    .filter((item) => selected.includes(item.id))
    .reduce((sum, item) => sum + parseFloat(item.final_fee || 0), 0);

  const discountAmount = subtotal * (tierDiscountPct / 100);
  const total = subtotal - discountAmount;

  const totalUsd = items
    .filter((item) => selected.includes(item.id))
    .reduce((sum, item) => sum + parseFloat(item.final_fee_usd || 0), 0);

  const tierBadgeLabel = useMemo(() => `${tier} discount`, [tier]);

  const onDrop = useCallback((files) => {
    if (files[0]) setScreenshot(files[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxFiles: 1
  });

  const handleSubmit = async () => {
    if (!selected.length) return toast.error('Select at least one item');

    setLoading(true);
    try {
      if (method === 'hubtel') {
        if (!paymentSettings.hubtel_enabled) {
          return toast.error('Hubtel direct payment is not available right now');
        }
        const res = await axios.post('/api/customer/payments/hubtel', { item_ids: selected });
        toast.success('Redirecting to Hubtel...');
        window.open(res.data.checkoutUrl, '_blank');
      } else {
        if (!screenshot) return toast.error('Please upload payment screenshot');

        const formData = new FormData();
        formData.append('screenshot', screenshot);
        formData.append('item_ids', JSON.stringify(selected));
        await axios.post('/api/customer/payments/manual', formData);

        toast.success('Payment submitted! Admin will verify shortly.');
        setSelected([]);
        setScreenshot(null);
        await loadItems();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <Link to="/home" className="back-btn">←</Link>
        <h1>Pay Now</h1>
      </div>

      <div className="page-content">
        {items.length === 0 ? (
          <div className="empty-state">
            <div className="icon">OK</div>
            <p style={{ fontWeight: 600 }}>No unpaid items</p>
            <p>All your items are paid up.</p>
          </div>
        ) : (
          <>
            <p className="section-title">Select Items to Pay</p>
            {items.map((item) => {
              const isSea = item.transport_mode === 'sea';
              const billedBy = isSea
                ? `${parseFloat(item.volume_cbm || 0).toFixed(4)} CBM`
                : `${parseFloat(item.weight_kg || 0).toFixed(2)} kg`;
              const modeLabel = isSea ? 'Sea' : 'Air';
              const isSelected = selected.includes(item.id);

              return (
                <div
                  key={item.id}
                  className="card"
                  onClick={() => toggle(item.id)}
                  style={{
                    cursor: 'pointer',
                    border: `1.5px solid ${isSelected ? '#1A6B8A' : '#e5e7eb'}`,
                    background: isSelected ? '#e8f4f8' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        border: `2px solid ${isSelected ? '#1A6B8A' : '#d1d5db'}`,
                        background: isSelected ? '#1A6B8A' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: 2
                      }}
                    >
                      {isSelected && <span style={{ color: 'white', fontSize: 12 }}>✓</span>}
                    </div>

                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{item.goods_name}</p>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 7px',
                            borderRadius: 20,
                            background: isSea ? '#e0f2fe' : '#f0fdf4',
                            color: isSea ? '#0369a1' : '#15803d'
                          }}
                        >
                          {modeLabel}
                        </span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          Billed by: <strong>{billedBy}</strong>
                        </span>
                      </div>

                      <div style={{ background: '#f8fafc', borderRadius: 8, padding: '6px 10px' }}>
                        {item.final_fee_usd && parseFloat(item.final_fee_usd) > 0 && (
                          <p style={{ fontSize: 11, color: '#6b7280' }}>
                            USD ${parseFloat(item.final_fee_usd).toFixed(2)}
                            {item.exchange_rate_used && (
                              <span style={{ color: '#9ca3af' }}> x {item.exchange_rate_used} GHS/USD</span>
                            )}
                          </p>
                        )}
                        {item.discount_applied > 0 && (
                          <p style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'line-through' }}>
                            GHS {parseFloat(item.shipping_fee).toFixed(2)}
                          </p>
                        )}
                        <p style={{ fontSize: 15, fontWeight: 800, color: '#1A6B8A' }}>
                          GHS {parseFloat(item.final_fee).toFixed(2)}
                        </p>
                        {item.discount_applied > 0 && (
                          <p style={{ fontSize: 10, color: '#16a34a' }}>-{item.discount_applied}% discount</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {selected.length > 0 && (
              <div className="card" style={{ background: '#1A6B8A', color: 'white', marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontWeight: 600 }}>{selected.length} item(s) selected</p>
                    {totalUsd > 0 && (
                      <p style={{ fontSize: 12, opacity: 0.8 }}>Approx. USD ${totalUsd.toFixed(2)}</p>
                    )}
                    {tierDiscountPct > 0 && (
                      <p style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                        Subtotal: GHS {subtotal.toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: 800, fontSize: 20 }}>GHS {total.toFixed(2)}</p>
                    {tierDiscountPct > 0 && (
                      <p style={{ fontSize: 11, background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '2px 8px', marginTop: 4 }}>
                        -{tierDiscountPct}% {tierBadgeLabel}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="section-title" style={{ marginTop: 16 }}>Payment Method</p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {[
                { key: 'manual', label: 'Mobile Money / Bank', desc: 'Upload screenshot for verification' },
                {
                  key: 'hubtel',
                  label: 'Hubtel (Direct Pay)',
                  desc: paymentSettings.hubtel_enabled ? 'Instant online payment' : 'Currently unavailable',
                  disabled: !paymentSettings.hubtel_enabled,
                }
              ].map((option) => (
                <div
                  key={option.key}
                  onClick={() => {
                    if (option.disabled) return;
                    setMethod(option.key);
                  }}
                  className="card"
                  style={{
                    flex: 1,
                    cursor: option.disabled ? 'not-allowed' : 'pointer',
                    opacity: option.disabled ? 0.6 : 1,
                    border: `1.5px solid ${method === option.key ? '#1A6B8A' : '#e5e7eb'}`,
                    background: method === option.key ? '#e8f4f8' : 'white',
                    padding: 12
                  }}
                >
                  <p style={{ fontSize: 13, fontWeight: 600 }}>{option.label}</p>
                  <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{option.desc}</p>
                </div>
              ))}
            </div>

            {method === 'manual' && (
              <>
                <p className="section-title">Upload Payment Screenshot</p>
                <div className="card" style={{ background: '#fffbeb', border: '1px solid #fef3c7', marginBottom: 12 }}>
                  <p style={{ fontSize: 13, color: '#92400e' }}>
                    Send payment to:
                    <br />
                    <strong>Mobile Money: {paymentSettings.manual_mobile_money_number || 'Not configured yet'}</strong>
                    <br />
                    Then upload your screenshot below.
                  </p>
                </div>

                <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
                  <input {...getInputProps()} />
                  {screenshot ? (
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>Selected: {screenshot.name}</p>
                      <p style={{ fontSize: 12, color: '#6b7280' }}>Tap to change</p>
                    </div>
                  ) : (
                    <div>
                      <p>Select a screenshot</p>
                      <p>Drag and drop it here, or <strong>tap to choose</strong></p>
                    </div>
                  )}
                </div>
              </>
            )}

            <button className="btn btn-primary btn-full" style={{ marginTop: 16 }} onClick={handleSubmit} disabled={loading || !selected.length}>
              {loading ? <span className="spinner" /> : method === 'hubtel' ? 'Pay with Hubtel' : 'Submit Payment'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
